import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useReporte, reporteActions } from '../../context/ReporteContext';
import { useEnviarReporte } from '../../hooks/useEnviarReporte';
import { useCorrelativo } from '../../hooks/useCorrelativo';
import { useMaquinas } from '../../hooks/useMaquinas';
import { useOperadores } from '../../hooks/useOperadores';
import { useFiltroMaquinasUsuario } from '../../hooks/useFiltroMaquinasUsuario';
import { useFiltroOperadoresUsuario } from '../../hooks/useFiltroOperadoresUsuario';
import { useListaItemsChequeo } from '../../hooks/useReferencias';
import { Button, Card } from '../../components/ui';
import { useInsumos } from '../../hooks/useInsumos';
import { ReportesService } from '../../services';
import ModalAgregarFaena from '../modales/ModalAgregarFaena';
import ModalAgregarInsumo from '../modales/ModalAgregarInsumo';
import ModalConfirmarEliminar from '../modales/ModalConfirmarEliminar';

interface FormularioReporteMovilProps {
  modoEdicion?: boolean;
  reporteId?: number;
  refetchReporte?: () => Promise<any>;
}

const FormularioReporteMovil: React.FC<FormularioReporteMovilProps> = ({
  modoEdicion = false,
  reporteId,
  refetchReporte,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useAuth();
  const {
    reporte,
    dispatch,
    estaCompleto,
    empresaId,
    nombreEmpresa,
    tieneEmpresaActiva,
    puedeCrearReporte,
    validacionHorometros,
  } = useReporte();

  const { estado: estadoEnvio, enviarReporte, limpiarEstado, reintentar } = useEnviarReporte();
  const { siguienteNumero: correlativo, cargando: cargandoCorrelativo } = useCorrelativo();

  // Hooks para actualizar reporte
  const useActualizarReporte = () => {
    const [actualizando, setActualizando] = useState(false);

    const actualizarReporte = async () => {
      if (!reporteId) {
        toast.error('ID de reporte no disponible');
        return;
      }

      setActualizando(true);

      try {
        const reporteTransformado = await ReportesService.transformarReporteParaBackend(reporte);
        await ReportesService.actualizarReporte(reporteId, reporteTransformado);

        if (refetchReporte) {
          await refetchReporte();
        }

        toast.success(t('reports.report_updated'));
        navigate(`/reportes/${reporteId}`);
      } catch (error: any) {
        console.error('Error actualizando reporte:', error);
        const mensaje = error?.message || 'Error al actualizar el reporte';
        toast.error(mensaje);
      } finally {
        setActualizando(false);
      }
    };

    return { actualizando, actualizarReporte };
  };

  const { actualizando, actualizarReporte } = useActualizarReporte();

  // Hooks para cargar datos
  const { maquinas, cargandoMaquinas } = useMaquinas();
  const { operadores, cargandoOperadores } = useOperadores();
  useInsumos();
  const { filtrarMaquinas, isLoading: isLoadingFiltro } = useFiltroMaquinasUsuario();
  const { filtrarOperadores, isLoading: isLoadingFiltroOperadores } = useFiltroOperadoresUsuario();
  const { data: itemsChequeoData, isLoading: cargandoItemsChequeo, error: errorItemsChequeo } = useListaItemsChequeo();

  const maquinasPermitidas = useMemo(() => {
    if (cargandoMaquinas || !maquinas) return [];
    return filtrarMaquinas(maquinas);
  }, [maquinas, filtrarMaquinas, cargandoMaquinas]);

  const operadoresPermitidos = useMemo(() => {
    if (cargandoOperadores || !operadores) return [];
    return filtrarOperadores(operadores);
  }, [operadores, filtrarOperadores, cargandoOperadores]);

  // Estados para wizard
  const [pasoActual, setPasoActual] = useState(1);
  const totalPasos = 6;

  // Estados para modales
  const [modalFaenaOpen, setModalFaenaOpen] = useState(false);
  const [modalEliminarOpen, setModalEliminarOpen] = useState(false);
  const [faenaAEliminar, setFaenaAEliminar] = useState<any>(null);
  const [faenaAEditar, setFaenaAEditar] = useState<any>(null);
  const [modalEditarFaenaOpen, setModalEditarFaenaOpen] = useState(false);
  const [modalCancelarOpen, setModalCancelarOpen] = useState(false);
  const [modalInsumoOpen, setModalInsumoOpen] = useState(false);
  const [modalEditarInsumoOpen, setModalEditarInsumoOpen] = useState(false);
  const [insumoAEditar, setInsumoAEditar] = useState<any>(null);
  const [insumoAEliminar, setInsumoAEliminar] = useState<any>(null);

  // Estados locales para inputs numéricos (permite coma/punto decimal)
  const [localHorometroInicial, setLocalHorometroInicial] = useState(reporte.horometro_inicial.toString());
  const [localHorometroFinal, setLocalHorometroFinal] = useState(reporte.horometro_final.toString());

  // Sincronizar estados locales cuando cambia el reporte (ej: al cargar en modo edición)
  useEffect(() => {
    setLocalHorometroInicial(reporte.horometro_inicial.toString());
    setLocalHorometroFinal(reporte.horometro_final.toString());
  }, [reporte.horometro_inicial, reporte.horometro_final]);

  // Handlers de faena
  const handleEditarFaena = (faena: any) => {
    setFaenaAEditar(faena);
    setModalEditarFaenaOpen(true);
  };

  const handleEliminarFaena = (faena: any) => {
    setFaenaAEliminar(faena);
    setModalEliminarOpen(true);
  };

  const confirmarEliminarFaena = () => {
    if (faenaAEliminar) {
      dispatch(reporteActions.eliminarFaena({ id_temporal: faenaAEliminar.id_temporal }));
      toast.success(`Faena "${faenaAEliminar.faena_codigo}" eliminada`);
      setModalEliminarOpen(false);
      setFaenaAEliminar(null);
    }
  };

  const cancelarEliminarFaena = () => {
    setModalEliminarOpen(false);
    setFaenaAEliminar(null);
  };

  // Handlers de insumo
  const handleEditarInsumo = (insumo: any) => {
    setInsumoAEditar(insumo);
    setModalEditarInsumoOpen(true);
  };

  const handleEliminarInsumo = (insumo: any) => {
    setInsumoAEliminar(insumo);
    setModalEliminarOpen(true);
  };

  const confirmarEliminarInsumo = () => {
    if (insumoAEliminar) {
      dispatch(reporteActions.eliminarInsumo({ id_temporal: insumoAEliminar.id_temporal }));
      toast.success(`Insumo "${insumoAEliminar.nombre_insumo}" eliminado`);
      setModalEliminarOpen(false);
      setInsumoAEliminar(null);
    }
  };

  const cancelarEliminarInsumo = () => {
    setModalEliminarOpen(false);
    setInsumoAEliminar(null);
  };

  // Handler de envío
  const handleEnviarReporte = async () => {
    if (!tieneEmpresaActiva) {
      toast.error('No hay empresa activa seleccionada');
      return;
    }

    if (!puedeCrearReporte) {
      toast.error('No tiene permisos para crear reportes en esta empresa');
      return;
    }

    if (!estaCompleto) {
      toast.error('Complete todos los campos obligatorios antes de enviar');
      return;
    }

    if (!empresaId) {
      toast.error('Error: No se pudo obtener el ID de la empresa');
      return;
    }

    try {
      await enviarReporte(reporte, empresaId);
    } catch (error: any) {
      console.error('❌ Error en handleEnviarReporte:', error);
    }
  };

  // Effects
  useEffect(() => {
    if (estadoEnvio.completado && estadoEnvio.reporteCreado) {
      const numeroReporte = estadoEnvio.reporteCreado.numero || 'Sin número';
      toast.success(`✅ Reporte #${numeroReporte} enviado exitosamente`);

      dispatch(reporteActions.limpiar());
      localStorage.setItem('refreshReportesList', 'true');
      navigate('/reportes');

      setTimeout(() => {
        limpiarEstado();
      }, 1000);
    }
  }, [estadoEnvio.completado, estadoEnvio.reporteCreado, dispatch, navigate, limpiarEstado]);

  useEffect(() => {
    if (estadoEnvio.error) {
      toast.error(`❌ Error al enviar reporte: ${estadoEnvio.error}`);
    }
  }, [estadoEnvio.error]);

  // 🆕 NUEVO: Cargar items de chequeo obligatorios cuando se selecciona una máquina
  useEffect(() => {
    // Solo cargar items si:
    // 1. Hay una máquina seleccionada
    // 2. Los items ya terminaron de cargar (no están en loading)
    // 3. Hay items de chequeo disponibles
    // 4. No estamos en modo edición (para evitar sobreescribir items ya cargados)

    if (!reporte.maquina_id) {
      return;
    }

    if (cargandoItemsChequeo) {
      return;
    }

    if (errorItemsChequeo) {
      return;
    }

    if (!itemsChequeoData || itemsChequeoData.length === 0) {
      return;
    }

    if (modoEdicion) {
      return;
    }

    const itemsObligatorios = itemsChequeoData.filter((item) => item.es_obligatorio);

    if (itemsObligatorios.length === 0) {
      return;
    }

    // Solo agregar items que no estén ya en la lista
    const itemsYaPresentes = reporte.items_chequeo.map(item => item.item_chequeo_id);
    const itemsNuevos = itemsObligatorios.filter(item => !itemsYaPresentes.includes(item.id));

    if (itemsNuevos.length > 0) {
      // Agregar cada item obligatorio
      itemsNuevos.forEach(item => {
        dispatch(reporteActions.agregarItemChequeo({
          id_temporal: `item_${item.id}_${Date.now()}`,
          item_chequeo_id: item.id,
          nombre_item: item.descripcion,
          verificado: false,
          horometro: 0,
          tiempo: 0,
          requiere_atencion: false,
          observaciones: ''
        }));
      });

      toast.success(`✅ Se agregaron ${itemsNuevos.length} items de chequeo obligatorios`);
    }
  }, [reporte.maquina_id, itemsChequeoData, cargandoItemsChequeo, errorItemsChequeo, modoEdicion]); // Omitir dispatch y reporte.items_chequeo de deps para evitar loop

  const handleCancelar = () => {
    setModalCancelarOpen(true);
  };

  const confirmarCancelar = () => {
    dispatch(reporteActions.limpiar());
    navigate('/reportes');
    setModalCancelarOpen(false);
  };

  const cancelarCancelar = () => {
    setModalCancelarOpen(false);
  };

  // Navegación entre pasos
  const siguientePaso = () => {
    if (pasoActual < totalPasos) {
      setPasoActual(pasoActual + 1);
      window.scrollTo(0, 0);
    }
  };

  const pasoAnterior = () => {
    if (pasoActual > 1) {
      setPasoActual(pasoActual - 1);
      window.scrollTo(0, 0);
    }
  };

  const irAPaso = (paso: number) => {
    setPasoActual(paso);
    window.scrollTo(0, 0);
  };

  // Validaciones por paso
  const pasoValido = () => {
    switch (pasoActual) {
      case 1: // Información básica
        return !!(reporte.maquina_id && reporte.fecha_reporte && reporte.numero_reporte);
      case 2: // Horómetros
        return reporte.horometro_inicial >= 0 &&
               reporte.horometro_final > 0 &&
               reporte.horometro_final > reporte.horometro_inicial;
      case 3: // Faenas - OBLIGATORIO: Debe haber faenas y las horas deben coincidir
        const horasTrabajadas = reporte.horometro_final - reporte.horometro_inicial;

        // Si no hay faenas, NO válido
        if (reporte.faenas.length === 0) {
          return false;
        }

        // Validar que las horas coincidan
        const sumaHorasFaenas = reporte.faenas.reduce((sum, faena) => sum + (faena.horas || 0), 0);
        // Permitir una diferencia de 0.1 horas (6 minutos) por redondeo
        const diferencia = Math.abs(horasTrabajadas - sumaHorasFaenas);
        const esValido = diferencia <= 0.1;

        return esValido;
      case 4: // Insumos
        return true; // Opcional
      case 5: // Items de chequeo
        return true; // Opcional
      case 6: // Estado y observaciones
        return !!reporte.estado_maquina;
      default:
        return true;
    }
  };

  // Mensaje de validación por paso
  const getMensajeValidacion = () => {
    switch (pasoActual) {
      case 1:
        if (!reporte.maquina_id) return 'Debe seleccionar una máquina';
        if (!reporte.fecha_reporte) return 'Debe seleccionar una fecha';
        if (!reporte.numero_reporte) return 'Debe ingresar un número de reporte';
        return '';
      case 2:
        if (reporte.horometro_inicial < 0) return 'Horómetro inicial debe ser mayor o igual a 0';
        if (reporte.horometro_final <= 0) return 'Horómetro final debe ser mayor a 0';
        if (reporte.horometro_final <= reporte.horometro_inicial) return 'Horómetro final debe ser mayor que el inicial';
        return '';
      case 3:
        // Si no hay faenas
        if (reporte.faenas.length === 0) {
          return 'Debe agregar al menos una faena';
        }
        // Si hay faenas, validar que las horas coincidan
        const horasTrabajadas = reporte.horometro_final - reporte.horometro_inicial;
        const sumaHorasFaenas = reporte.faenas.reduce((sum, faena) => sum + (faena.horas || 0), 0);
        const diferencia = Math.abs(horasTrabajadas - sumaHorasFaenas);
        if (diferencia > 0.1) {
          return `Las horas de faenas (${sumaHorasFaenas.toFixed(1)}h) no coinciden con las horas trabajadas (${horasTrabajadas.toFixed(1)}h)`;
        }
        return '';
      case 6:
        if (!reporte.estado_maquina) return 'Debe seleccionar el estado de la máquina';
        return '';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Sticky */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleCancelar}
              className="text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
            <h1 className="text-lg font-bold text-gray-900 text-center flex-1">
              {modoEdicion ? t('reports.edit_report') : t('reports.new_report')}
            </h1>
            <div className="w-6"></div> {/* Spacer para centrar título */}
          </div>

          {/* Indicador de progreso */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              {t('common.page')} {pasoActual} {t('common.of')} {totalPasos}
            </span>
            <span className="text-sm text-gray-600">
              {Math.round((pasoActual / totalPasos) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(pasoActual / totalPasos) * 100}%` }}
            ></div>
          </div>

          {/* Indicadores de pasos */}
          <div className="flex justify-between mt-3 overflow-x-auto pb-2">
            {[1, 2, 3, 4, 5, 6].map((paso) => (
              <button
                key={paso}
                onClick={() => irAPaso(paso)}
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                  pasoActual === paso
                    ? 'bg-blue-600 text-white'
                    : pasoActual > paso
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {pasoActual > paso ? '✓' : paso}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenido del paso */}
      <div className="p-4 pb-40">
        {/* Paso 1: Información Básica */}
        {pasoActual === 1 && (
          <Card>
            <Card.Header>
              <h2 className="text-xl font-bold">📝 {t('reports.title')}</h2>
              <p className="text-sm text-gray-600 mt-1">{nombreEmpresa}</p>
            </Card.Header>
            <Card.Content>
              <div className="space-y-4">
                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.machine')} *
                  </label>
                  <select
                    value={reporte.maquina_id || ''}
                    onChange={(e) =>
                      dispatch(
                        reporteActions.actualizarBasico({
                          maquina_id: parseInt(e.target.value) || null,
                        })
                      )
                    }
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-4 text-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer min-h-[56px]"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 1rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.25em 1.25em',
                      paddingRight: '3rem'
                    }}
                    disabled={cargandoMaquinas || isLoadingFiltro}
                  >
                    <option value="">
                      {cargandoMaquinas || isLoadingFiltro
                        ? t('reports.loading_machines')
                        : t('reports.select_machine')}
                    </option>
                    {maquinasPermitidas.map((maquina) => (
                      <option key={maquina.id} value={maquina.id}>
                        {maquina.codigo} - {maquina.descripcion}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.operator')}
                  </label>
                  <select
                    value={reporte.operador_id || ''}
                    onChange={(e) =>
                      dispatch(
                        reporteActions.actualizarBasico({
                          operador_id: parseInt(e.target.value) || null,
                        })
                      )
                    }
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-4 text-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer min-h-[56px]"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 1rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.25em 1.25em',
                      paddingRight: '3rem'
                    }}
                    disabled={cargandoOperadores || isLoadingFiltroOperadores}
                  >
                    <option value="">
                      {cargandoOperadores || isLoadingFiltroOperadores
                        ? t('reports.loading_operators')
                        : t('reports.select_operator')}
                    </option>
                    {operadoresPermitidos.map((operador) => (
                      <option key={operador.id} value={operador.id}>
                        {operador.codigo}
                        {operador.nombre_completo
                          ? ` - ${operador.nombre_completo}`
                          : ` - ${operador.descripcion}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.date')} *
                  </label>
                  <input
                    type="date"
                    value={reporte.fecha_reporte}
                    onChange={(e) =>
                      dispatch(
                        reporteActions.actualizarBasico({
                          fecha_reporte: e.target.value,
                        })
                      )
                    }
                    className="w-full border rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.report_number')}
                  </label>
                  <input
                    type="text"
                    value={
                      reporte.numero_reporte.toLowerCase() === 'e' && correlativo
                        ? correlativo
                        : reporte.numero_reporte
                    }
                    onChange={(e) => {
                      dispatch(
                        reporteActions.actualizarBasico({
                          numero_reporte: e.target.value,
                        })
                      );
                    }}
                    placeholder="e (automático) o 123 (manual)"
                    className="w-full border rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500"
                  />
                  {reporte.numero_reporte.toLowerCase() === 'e' && (
                    <p className="text-sm text-green-600 mt-2">
                      {cargandoCorrelativo
                        ? 'Obteniendo correlativo...'
                        : correlativo
                        ? `✨ Se generará: ${correlativo}`
                        : '✨ Correlativo automático'}
                    </p>
                  )}
                </div>
              </div>
            </Card.Content>
          </Card>
        )}

        {/* Paso 2: Horómetros */}
        {pasoActual === 2 && (
          <Card>
            <Card.Header>
              <h2 className="text-xl font-bold">⏱️ {t('checklist.horometer')}s</h2>
            </Card.Header>
            <Card.Content>
              <div className="space-y-4">
                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.initial_horometer')} *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={localHorometroInicial}
                    onChange={(e) => {
                      const stringValue = e.target.value;
                      setLocalHorometroInicial(stringValue);

                      // Convertir coma a punto y parsear
                      const normalized = stringValue.replace(',', '.');
                      const num = parseFloat(normalized);

                      if (!isNaN(num) && num >= 0) {
                        dispatch(reporteActions.actualizarHorometros(num, reporte.horometro_final));
                      } else if (stringValue === '' || stringValue === '0') {
                        dispatch(reporteActions.actualizarHorometros(0, reporte.horometro_final));
                      }
                    }}
                    onBlur={() => {
                      // Limpiar formato al salir del campo
                      const normalized = localHorometroInicial.replace(',', '.');
                      const num = parseFloat(normalized);
                      setLocalHorometroInicial(isNaN(num) ? '0' : num.toString());
                    }}
                    pattern="[0-9]*[.,]?[0-9]*"
                    placeholder="0.0"
                    className="w-full border rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.final_horometer')} *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={localHorometroFinal}
                    onChange={(e) => {
                      const stringValue = e.target.value;
                      setLocalHorometroFinal(stringValue);

                      // Convertir coma a punto y parsear
                      const normalized = stringValue.replace(',', '.');
                      const num = parseFloat(normalized);

                      if (!isNaN(num) && num >= 0) {
                        dispatch(reporteActions.actualizarHorometros(reporte.horometro_inicial, num));
                      } else if (stringValue === '' || stringValue === '0') {
                        dispatch(reporteActions.actualizarHorometros(reporte.horometro_inicial, 0));
                      }
                    }}
                    onBlur={() => {
                      // Limpiar formato al salir del campo
                      const normalized = localHorometroFinal.replace(',', '.');
                      const num = parseFloat(normalized);
                      setLocalHorometroFinal(isNaN(num) ? '0' : num.toString());
                    }}
                    pattern="[0-9]*[.,]?[0-9]*"
                    placeholder="0.0"
                    className="w-full border rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.worked_hours')}
                  </label>
                  <input
                    type="number"
                    value={reporte.horas_trabajadas}
                    disabled
                    className="w-full border rounded-lg px-4 py-3 text-base bg-gray-100 text-gray-600"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t('reports.worked_hours_auto')}
                  </p>
                </div>

                {!validacionHorometros.esValido &&
                  validacionHorometros.mensaje && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600 font-medium">
                        ⚠️ {validacionHorometros.mensaje}
                      </p>
                    </div>
                  )}

                {validacionHorometros.esValido && reporte.faenas.length > 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-600">
                      ✅ Horas coinciden correctamente
                    </p>
                  </div>
                )}
              </div>
            </Card.Content>
          </Card>
        )}

        {/* Paso 3: Faenas */}
        {pasoActual === 3 && (
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">📋 {t('faenas.title')}</h2>
                <Button
                  size="sm"
                  onClick={() => setModalFaenaOpen(true)}
                  disabled={!tieneEmpresaActiva || !puedeCrearReporte}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  ➕
                </Button>
              </div>
            </Card.Header>
            <Card.Content>
              {/* Indicador de horas */}
              {reporte.faenas.length > 0 && (() => {
                const horasTrabajadas = reporte.horometro_final - reporte.horometro_inicial;
                const sumaHorasFaenas = reporte.faenas.reduce((sum, f) => sum + (f.horas || 0), 0);
                const diferencia = Math.abs(horasTrabajadas - sumaHorasFaenas);
                const esValido = diferencia <= 0.1;

                return (
                  <div className={`mb-4 p-4 rounded-lg border-2 ${
                    esValido
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-700 font-medium">{t('reports.worked_hours')}:</span>
                      <span className="font-bold text-gray-900 text-base">
                        {horasTrabajadas.toFixed(1)}h
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-gray-700 font-medium">{t('reports.faena_hours_sum')}:</span>
                      <span className="font-bold text-gray-900 text-base">
                        {sumaHorasFaenas.toFixed(1)}h
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      {esValido ? (
                        <div className="flex items-center text-green-700 font-semibold">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          ✓ {t('reports.horometer_match')}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-center text-red-700 font-semibold">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            ✗ {t('reports.difference')}: {diferencia.toFixed(1)}h
                          </div>
                          <p className="text-xs text-red-600 ml-7">
                            Ajusta las horas de las faenas para que coincidan con las horas trabajadas
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}


              {reporte.faenas.length === 0 ? (
                <div className="text-center py-8">
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-4">
                    <svg className="w-12 h-12 text-yellow-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-gray-900 font-semibold mb-1">⚠️ {t('faenas.title')}</p>
                    <p className="text-sm text-gray-700">{t('faenas.faena_required')}</p>
                    <p className="text-xs text-gray-600 mt-2">{t('reports.horometer_mismatch')}</p>
                  </div>
                  <Button
                    onClick={() => setModalFaenaOpen(true)}
                    disabled={!tieneEmpresaActiva || !puedeCrearReporte}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {t('faenas.add_faena')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {reporte.faenas.map((faena) => (
                    <div
                      key={faena.id_temporal}
                      className="border rounded-lg p-3 bg-white shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-base">
                            {faena.faena_codigo}
                          </p>
                          <p className="text-sm text-gray-600">
                            {faena.descripcion}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          <button
                            className="text-blue-500 text-xl"
                            onClick={() => handleEditarFaena(faena)}
                          >
                            ✏️
                          </button>
                          <button
                            className="text-red-500 text-xl"
                            onClick={() => handleEliminarFaena(faena)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>🏠 Predio: {faena.predio_codigo}</p>
                        <p>🌳 Rodal: {faena.rodal_codigo}</p>
                        <p>⏱️ Horas: {faena.horas}h</p>
                        <p>📏 Área: {faena.area_trabajada} ha</p>
                        {faena.archivos_kml.length > 0 && (
                          <p className="text-green-600">
                            🗺️ {faena.archivos_kml.length} archivo(s) KML
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Content>
          </Card>
        )}

        {/* Paso 4: Insumos */}
        {pasoActual === 4 && (
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">📦 {t('supplies.title')}</h2>
                <Button
                  size="sm"
                  onClick={() => setModalInsumoOpen(true)}
                  disabled={!tieneEmpresaActiva || !puedeCrearReporte}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  ➕
                </Button>
              </div>
            </Card.Header>
            <Card.Content>
              {reporte.insumos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-2">{t('supplies.no_supplies')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reporte.insumos.map((insumo) => (
                    <div
                      key={insumo.id_temporal}
                      className="border rounded-lg p-3 bg-white shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium text-base">
                            {insumo.nombre_insumo}
                          </p>
                          <p className="text-sm text-gray-600">
                            {insumo.cantidad} {insumo.unidad}
                          </p>
                          {insumo.costo_unitario && insumo.costo_unitario > 0 && (
                            <p className="text-sm text-gray-600">
                              💰 $
                              {(
                                insumo.cantidad * insumo.costo_unitario
                              ).toLocaleString('es-CL')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 ml-2">
                          <button
                            className="text-blue-500 text-xl"
                            onClick={() => handleEditarInsumo(insumo)}
                          >
                            ✏️
                          </button>
                          <button
                            className="text-red-500 text-xl"
                            onClick={() => handleEliminarInsumo(insumo)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {reporte.insumos.some(
                    (i) => i.costo_unitario && i.costo_unitario > 0
                  ) && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-700 font-medium">
                        {t('supplies.total_cost')}: $
                        {reporte.insumos
                          .reduce((total, insumo) => {
                            const costo =
                              (insumo.costo_unitario || 0) * insumo.cantidad;
                            return total + costo;
                          }, 0)
                          .toLocaleString('es-CL')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Card.Content>
          </Card>
        )}

        {/* Paso 5: Items de Chequeo */}
        {pasoActual === 5 && (
          <Card>
            <Card.Header>
              <h2 className="text-xl font-bold">✅ {t('checklist.title')}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {reporte.items_chequeo.filter((i) => i.verificado).length} de{' '}
                {reporte.items_chequeo.length} completados
              </p>
            </Card.Header>
            <Card.Content>
              {reporte.items_chequeo.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('checklist.no_items')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reporte.items_chequeo.map((item) => (
                    <div
                      key={item.id_temporal}
                      className="border rounded-lg p-4 bg-white"
                    >
                      {/* Checkbox principal */}
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.verificado}
                          onChange={(e) => {
                            dispatch(reporteActions.actualizarItemChequeo(
                              item.id_temporal,
                              { verificado: e.target.checked }
                            ));
                          }}
                          className="w-6 h-6 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                        />
                        <span className="font-medium text-base flex-1">
                          {item.nombre_item}
                        </span>
                        {item.requiere_atencion && (
                          <span className="text-red-500 text-sm">⚠️</span>
                        )}
                      </label>

                      {/* Campos adicionales si está verificado */}
                      {item.verificado && (
                        <div className="mt-4 space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Horómetro *
                            </label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={item.horometro || ''}
                              onChange={(e) => {
                                const stringValue = e.target.value;
                                const normalized = stringValue.replace(',', '.');
                                const num = parseFloat(normalized);

                                if (!isNaN(num) && num >= 0) {
                                  dispatch(reporteActions.actualizarItemChequeo(
                                    item.id_temporal,
                                    { horometro: num }
                                  ));
                                } else if (stringValue === '' || stringValue === '0') {
                                  dispatch(reporteActions.actualizarItemChequeo(
                                    item.id_temporal,
                                    { horometro: 0 }
                                  ));
                                }
                              }}
                              pattern="[0-9]*[.,]?[0-9]*"
                              placeholder="Ej: 1234.5"
                              className="w-full border-2 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Tiempo (minutos) *
                            </label>
                            <input
                              type="number"
                              value={item.tiempo || ''}
                              onChange={(e) => {
                                dispatch(reporteActions.actualizarItemChequeo(
                                  item.id_temporal,
                                  { tiempo: parseInt(e.target.value) || 0 }
                                ));
                              }}
                              min="1"
                              max="480"
                              placeholder="Ej: 15"
                              className="w-full border-2 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Máximo 480 minutos (8 horas)
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Observaciones
                            </label>
                            <textarea
                              value={item.observaciones || ''}
                              onChange={(e) => {
                                dispatch(reporteActions.actualizarItemChequeo(
                                  item.id_temporal,
                                  { observaciones: e.target.value }
                                ));
                              }}
                              rows={3}
                              placeholder="Observaciones del chequeo..."
                              className="w-full border-2 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Content>
          </Card>
        )}

        {/* Paso 6: Estado y Observaciones */}
        {pasoActual === 6 && (
          <Card>
            <Card.Header>
              <h2 className="text-xl font-bold">🔧 {t('reports.machine_status')} & {t('reports.observations')}</h2>
            </Card.Header>
            <Card.Content>
              <div className="space-y-4">
                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.machine_status')} *
                  </label>
                  <select
                    value={reporte.estado_maquina}
                    onChange={(e) =>
                      dispatch(
                        reporteActions.actualizarBasico({
                          estado_maquina: e.target.value as any,
                        })
                      )
                    }
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-4 text-lg font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer min-h-[56px]"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 1rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.25em 1.25em',
                      paddingRight: '3rem'
                    }}
                  >
                    <option value="OPERATIVA">✅ {t('reports.machine_status_operative')}</option>
                    <option value="FALLA_MENOR">⚠️ {t('reports.machine_status_minor_fault')}</option>
                    <option value="FALLA_MAYOR">🔴 {t('reports.machine_status_major_fault')}</option>
                    <option value="MANTENIMIENTO">🔧 {t('reports.machine_status_maintenance')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.observations')}
                  </label>
                  <textarea
                    value={reporte.observaciones}
                    onChange={(e) =>
                      dispatch(
                        reporteActions.actualizarBasico({
                          observaciones: e.target.value,
                        })
                      )
                    }
                    rows={4}
                    placeholder="Observaciones del día de trabajo..."
                    className="w-full border rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 resize-y"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium mb-2">
                    {t('reports.news')}
                  </label>
                  <textarea
                    value={reporte.novedades}
                    onChange={(e) =>
                      dispatch(
                        reporteActions.actualizarBasico({
                          novedades: e.target.value,
                        })
                      )
                    }
                    rows={4}
                    placeholder="Novedades o incidentes..."
                    className="w-full border rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 resize-y"
                  />
                </div>
              </div>
            </Card.Content>
          </Card>
        )}
      </div>

      {/* Footer con navegación - z-index mayor que MobileNavigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-[10000] shadow-lg">
        {/* Mensaje de validación */}
        {!pasoValido() && getMensajeValidacion() && (
          <div className="px-4 pt-3 pb-2">
            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700 leading-tight">
              ⚠️ {getMensajeValidacion()}
            </div>
          </div>
        )}

        <div className="px-4 pb-4 pt-2">

        <div className="flex items-center justify-between gap-3">
          {pasoActual > 1 ? (
            <Button
              variant="outline"
              onClick={pasoAnterior}
              className="flex-1"
            >
              ← {t('common.previous')}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleCancelar}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
          )}

          {pasoActual < totalPasos ? (
            <Button
              onClick={siguientePaso}
              disabled={!pasoValido()}
              className={`flex-1 ${
                pasoValido()
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('common.next')} →
            </Button>
          ) : (
            <Button
              onClick={modoEdicion ? actualizarReporte : handleEnviarReporte}
              disabled={
                modoEdicion
                  ? actualizando || !estaCompleto
                  : !estaCompleto ||
                    !tieneEmpresaActiva ||
                    !puedeCrearReporte ||
                    estadoEnvio.enviando
              }
              className={`flex-1 ${
                estaCompleto &&
                (modoEdicion ||
                  (tieneEmpresaActiva &&
                    puedeCrearReporte &&
                    !estadoEnvio.enviando))
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {modoEdicion
                ? actualizando
                  ? t('common.saving')
                  : t('common.save')
                : estadoEnvio.enviando
                ? t('reports.sending_report')
                : t('reports.submit_report')}
            </Button>
          )}
        </div>
        </div>
      </div>

      {/* Modales */}
      <ModalAgregarFaena
        isOpen={modalFaenaOpen}
        onClose={() => setModalFaenaOpen(false)}
      />

      <ModalAgregarFaena
        isOpen={modalEditarFaenaOpen}
        onClose={() => {
          setModalEditarFaenaOpen(false);
          setFaenaAEditar(null);
        }}
        faenaParaEditar={faenaAEditar}
        modoEdicion={true}
      />

      <ModalAgregarInsumo
        isOpen={modalInsumoOpen}
        onClose={() => setModalInsumoOpen(false)}
      />

      <ModalAgregarInsumo
        isOpen={modalEditarInsumoOpen}
        onClose={() => {
          setModalEditarInsumoOpen(false);
          setInsumoAEditar(null);
        }}
        insumoParaEditar={insumoAEditar}
        modoEdicion={true}
      />

      <ModalConfirmarEliminar
        isOpen={modalCancelarOpen}
        titulo="Cancelar Creación de Reporte"
        mensaje="¿Estás seguro de cancelar?"
        detalles={[
          'Se perderán todos los datos ingresados',
          'Esta acción no se puede deshacer',
        ]}
        onConfirmar={confirmarCancelar}
        onCancelar={cancelarCancelar}
        confirmText="Sí, cancelar"
        cancelText="Continuar"
        tipo="warning"
      />

      <ModalConfirmarEliminar
        isOpen={modalEliminarOpen}
        titulo={faenaAEliminar ? 'Eliminar Faena' : 'Eliminar Insumo'}
        mensaje={
          faenaAEliminar
            ? `¿Eliminar "${faenaAEliminar?.faena_codigo}"?`
            : `¿Eliminar "${insumoAEliminar?.nombre_insumo}"?`
        }
        detalles={['Esta acción no se puede deshacer']}
        onConfirmar={
          faenaAEliminar ? confirmarEliminarFaena : confirmarEliminarInsumo
        }
        onCancelar={
          faenaAEliminar ? cancelarEliminarFaena : cancelarEliminarInsumo
        }
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        tipo="danger"
      />

      {/* Modal de envío */}
      {estadoEnvio.enviando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <div className="text-center">
              <div className="mb-4">
                <svg
                  className="animate-spin h-12 w-12 text-blue-600 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Enviando Reporte
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {estadoEnvio.pasoActual}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${estadoEnvio.progreso}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {estadoEnvio.progreso}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de error */}
      {estadoEnvio.error && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <div className="text-center">
              <div className="mb-4">
                <svg
                  className="h-12 w-12 text-red-600 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error al Enviar
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {estadoEnvio.error}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={limpiarEstado}
                  className="flex-1"
                >
                  Cerrar
                </Button>
                <Button
                  onClick={reintentar}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Reintentar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormularioReporteMovil;
